/**
 * Per-source attention reload for ZuneloContext.retry (keeps retry CRAP ≤ 30).
 */

import type {
  AgentItem,
  AttentionItem,
  CurrentUserIdentity,
  GmailMessage,
  GitHubPullRequest,
  JiraIssue,
  SlackThread,
  AgentSession,
  ReminderItem,
  NoteItem,
  DailyGroup,
  LlmWikiPage,
  WorkContextGraph,
} from '../domain/types';
import { mapAgentSessionToAgentItem } from '../domain/mappers';
import { CONNECTABLE_SOURCES, isConnectableSource } from '../domain/providerIds';
import { applyAttentionSource, ATTENTION_SOURCE_LOADERS } from './sourceLoaders';
import { agentsClient, workspaceClient } from '../services/clients';
import { withForcedListReads } from '../services/cache/wrapListClient';

export type RetrySourceDeps = {
  currentUser?: CurrentUserIdentity;
  setItems: (updater: (prev: AttentionItem[]) => AttentionItem[]) => void;
  setRawJiraIssues: (value: JiraIssue[]) => void;
  setRawPullRequests: (value: GitHubPullRequest[]) => void;
  setRawSlackThreads: (value: SlackThread[]) => void;
  setRawAgentSessions: (value: AgentSession[]) => void;
  setAgents: (value: AgentItem[]) => void;
  setRawGmailMessages: (value: GmailMessage[]) => void;
  setReminders?: (value: ReminderItem[]) => void;
  setNotes?: (value: NoteItem[]) => void;
  setDailyGroups?: (value: DailyGroup[]) => void;
  setLlmWiki?: (value: LlmWikiPage[]) => void;
  setWorkContextGraph?: (value: WorkContextGraph) => void;
};

type SourceLoader = (deps: RetrySourceDeps) => Promise<void>;

const SOURCE_LOADERS: Record<string, SourceLoader> = {
  agents: reloadAgents,
  workspace: reloadWorkspace,
};

export async function reloadAttentionSource(source: string, deps: RetrySourceDeps): Promise<void> {
  if (isConnectableSource(source)) {
    await withForcedListReads(() => reloadProviderSource(deps, source));
    return;
  }
  const loader = SOURCE_LOADERS[source];
  if (loader) await withForcedListReads(() => loader(deps));
}

/**
 * Reloads one connectable source through its `ATTENTION_SOURCE_LOADERS`
 * entry — the same fetch/map used by bootstrap — so retry preserves
 * `st`/`ai`/`next` from the matching previous item instead of resetting it.
 */
async function reloadProviderSource(
  deps: RetrySourceDeps,
  source: (typeof CONNECTABLE_SOURCES)[number],
): Promise<void> {
  const res = await ATTENTION_SOURCE_LOADERS[source].fetch();
  applyAttentionSource(deps, source, res.data);
}

async function reloadAgents(deps: RetrySourceDeps): Promise<void> {
  const res = await agentsClient.getSessions();
  deps.setRawAgentSessions(res.data);
  deps.setAgents(res.data.map(mapAgentSessionToAgentItem));
}

/**
 * Refetches the shared workspace document (reminders, notes, Daily groups,
 * LLM wiki, work-context graph) and writes it back through the slice
 * setters. This makes every `retry('workspace')` call across the
 * reminders/notes/Daily/wiki/work-context mutators a real recovery path
 * instead of a silent no-op.
 */
async function reloadWorkspace(deps: RetrySourceDeps): Promise<void> {
  const [reminders, notes, dailyGroups, llmWiki, workContextGraph] = await Promise.all([
    workspaceClient.getReminders(),
    workspaceClient.getNotes(),
    workspaceClient.getDailyGroups(),
    workspaceClient.getLlmWiki(),
    workspaceClient.getWorkContextGraph(),
  ]);
  deps.setReminders?.(reminders.data);
  deps.setNotes?.(notes.data);
  deps.setDailyGroups?.(dailyGroups.data);
  deps.setLlmWiki?.(llmWiki.data);
  deps.setWorkContextGraph?.(workContextGraph.data);
}
